import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface AsyncResource<T> {
  data: T | undefined;
  loading: boolean;
  /** Reads again even when a load is in flight, so a just-made change is not lost behind a stale read. */
  reload: () => void;
  error: string | undefined;
  /** Fetch fresh data unless a request is already in flight. */
  ensureLoaded: () => void;
  /** Fetch fresh data using the same concurrent-request guard. */
  refresh: () => void;
}

/** Fresh loads with stable callbacks and concurrent-request protection. */
export function useAsyncResource<T>(fetcher: () => Promise<T>): AsyncResource<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Transient control flags that must not trigger re-renders.
  const inFlightRef = useRef<boolean>(false);
  // Keep the latest fetcher without changing `load`'s identity (useLatest pattern).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setLoading(true);
    setError(undefined);
    try {
      const result = await fetcherRef.current();
      setData(result);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unknown error');
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, []);

  const ensureLoaded = useCallback(() => {
    void load();
  }, [load]);
  // A fresh read even while one is in flight: that one may predate the change being shown (a report
  // just created), so it runs once more when the current load settles instead of being dropped.
  const pendingReloadRef = useRef<boolean>(false);
  const reload = useCallback(() => {
    if (!inFlightRef.current) {
      void load();
      return;
    }
    pendingReloadRef.current = true;
  }, [load]);
  useEffect(() => {
    if (!loading && pendingReloadRef.current) {
      pendingReloadRef.current = false;
      void load();
    }
  }, [loading, load]);
  return useMemo(() => ({ data, loading, error, ensureLoaded, refresh: ensureLoaded, reload }), [data, loading, error, ensureLoaded, reload]);
}

/**
 * Derived "is this resource still pending" flag: true until the first load resolves with either
 * data or an error, and while a refetch is in flight. Lets views show a spinner on first paint
 * (before `ensureLoaded`'s effect has run) without a flash of the empty state.
 */
export function isResourcePending<T>(resource: AsyncResource<T>): boolean {
  return resource.loading || (resource.data === undefined && resource.error === undefined);
}
